import { useState, useCallback, useEffect } from 'react'
import TransactionsService from '../../services/intellijetnet/transactions.service'
import { Transaction } from '../../types/intellijetnet'

// Hook for fetching a single transaction by ID
export function useTransactionById(transId: number | null) {
  const [data, setData] = useState<Transaction | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetch = useCallback(async () => {
    if (!transId) return

    setLoading(true)
    setError(null)
    try {
      const transaction = await TransactionsService.getById(transId)
      setData(transaction)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [transId])

  useEffect(() => {
    fetch()
  }, [fetch])

  return { data, loading, error, refetch: fetch }
}

// Hook for fetching transactions by model ID
export function useTransactionsByModelId(modelId: number | null) {
  const [data, setData] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetch = useCallback(async () => {
    if (!modelId) return

    setLoading(true)
    setError(null)
    try {
      const transactions = await TransactionsService.getByModelId(modelId)
      setData(transactions)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [modelId])

  useEffect(() => {
    fetch()
  }, [fetch])

  return { data, loading, error, refetch: fetch }
}

// Hook for fetching transactions by aircraft ID
export function useTransactionsByAircraftId(aircraftId: number | null) {
  const [data, setData] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetch = useCallback(async () => {
    if (!aircraftId) return

    setLoading(true)
    setError(null)
    try {
      const transactions = await TransactionsService.getByAircraftId(aircraftId)
      setData(transactions)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [aircraftId])

  useEffect(() => {
    fetch()
  }, [fetch])

  return { data, loading, error, refetch: fetch }
}

// Hook for fetching transactions within a date range
export function useTransactionsByDateRange(startDate: string | null, endDate: string | null) {
  const [data, setData] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetch = useCallback(async () => {
    if (!startDate || !endDate) return

    setLoading(true)
    setError(null)
    try {
      const transactions = await TransactionsService.getByDateRange(startDate, endDate)
      setData(transactions)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => {
    fetch()
  }, [fetch])

  return { data, loading, error, refetch: fetch }
}

// Hook for filtered transactions search
export function useTransactionsFiltered(filters: {
  modelId?: number
  aircraftId?: number
  startDate?: string
  endDate?: string
  transactionTypes?: string[]
  limit?: number
}) {
  const [data, setData] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const transactions = await TransactionsService.getFiltered(filters)
      setData(transactions)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.modelId, filters.aircraftId, filters.startDate, filters.endDate, filters.transactionTypes, filters.limit])

  useEffect(() => {
    fetch()
  }, [fetch])

  return { data, loading, error, refetch: fetch }
}